import { useEffect, useState } from "react";

import { Button, View, EmptyState, Text } from "./base";
import { Icon } from "./base/Icon";
import { InputField } from "./base/InputField";
import { useAppSelector } from "../redux";
import { SelectField } from "./base/SelectField";
import { AgentEnvironmentVariable } from "../redux/reducers/agentEnvironmentVariableSlice";
import { Tooltip } from "./base/Tooltip";
import { ConfirmDialog } from "./ConfirmDialog";
import { IconButton } from "./base/IconButton";

type VariablesFormProps = {
  variables?: Partial<AgentEnvironmentVariable>[];
  onChange: (newVariable: Partial<AgentEnvironmentVariable>) => void;
  onClickAdd: () => void;
  onClickDelete: (id: string) => Promise<void>;
};

export const VariablesForm = ({ variables = [], onChange, onClickAdd, onClickDelete }: VariablesFormProps) => {
  const [internalVariables, setInternalVariables] = useState<Partial<AgentEnvironmentVariable>[]>(variables);
  const [isLoadingDeleteId, setIsLoadingDeleteId] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingVariable, setDeletingVariable] = useState<Partial<AgentEnvironmentVariable> | null>(null);
  const isLoadingCreate = useAppSelector((state) => state.agentEnvironmentVariable.isLoadingCreate);

  const onChangeHandler = (id: string, key: string) => (value: string) => {
    const currentVariable = internalVariables.find((v) => v.id === id);
    const newVariable = { ...currentVariable, [key]: value };

    if (key === "masked") {
      newVariable.masked = value === "masked";
    }

    onChange(newVariable);
    setInternalVariables((prev) => prev.map((v) => (v.id === id ? newVariable : v)));
  };

  const handleDeleteClick = (variable: Partial<AgentEnvironmentVariable>) => {
    setDeletingVariable(variable);
    setShowDeleteDialog(true);
  };

  const confirmDelete = async () => {
    if (!deletingVariable || !deletingVariable.id) return;

    setIsLoadingDeleteId(deletingVariable.id);
    setShowDeleteDialog(false);

    await onClickDelete(deletingVariable.id);

    setIsLoadingDeleteId(null);
    setDeletingVariable(null);
  };

  useEffect(() => {
    if (variables.length !== internalVariables.length) {
      setInternalVariables(variables);
    }
  }, [variables]);

  if (!internalVariables.length) {
    return (
      <EmptyState
        icon="list"
        title="No Agent Variables Found"
        description="Add your first variable to get started. Variables help you store information for use with your agents."
        actionLabel="Add Your First Variable"
        onAction={onClickAdd}
        className="w-full flex-1"
      />
    );
  }

  return (
    <View className="flex flex-col flex-1 w-full gap-2">
      {internalVariables
        .sort((a, b) => {
          const aDate = new Date(a.createdAt || 0).getTime();
          const bDate = new Date(b.createdAt || 0).getTime();

          return aDate - bDate;
        })
        .map((variable, index) => (
          <View
            key={index}
            className="flex w-full gap-2 items-center"
          >
            {variable.masked ? (
              <>
                <Tooltip
                  content="Masked variables cannot be edited."
                  className="w-full"
                >
                  <InputField
                    value={variable.name}
                    placeholder="Variable name"
                    isDisabled
                  />
                </Tooltip>
                <Tooltip
                  content="Masked variables cannot be edited."
                  className="w-full"
                >
                  <InputField
                    type="password"
                    value={variable.value}
                    placeholder="Variable value"
                    isDisabled
                  />
                </Tooltip>
                <Tooltip content="Masked variables cannot be edited.">
                  <SelectField
                    value="masked"
                    className="!w-36"
                    labelClassName="!w-36"
                    isDisabled
                    options={[{ label: "Masked", value: "masked" }]}
                  />
                </Tooltip>
              </>
            ) : (
              <>
                <InputField
                  value={variable.name}
                  onChange={onChangeHandler(variable.id || "", "name")}
                  placeholder="Variable name"
                />
                <InputField
                  type={variable.masked ? "password" : "text"}
                  value={variable.value}
                  onChange={onChangeHandler(variable.id || "", "value")}
                  placeholder="Variable value"
                />
                <SelectField
                  value={variable.masked ? "masked" : "visible"}
                  onChange={onChangeHandler(variable.id || "", "masked")}
                  className="!w-36"
                  labelClassName="!w-36"
                  options={[
                    { label: "Visible", value: "visible" },
                    { label: "Masked", value: "masked" },
                  ]}
                />
              </>
            )}
            <IconButton
              variant="danger"
              icon="trash"
              onClick={() => handleDeleteClick(variable)}
              isLoading={isLoadingDeleteId === variable.id}
            />
          </View>
        ))}

      <Button
        variant="secondary"
        className="self-end mt-2"
        onClick={onClickAdd}
        isLoading={isLoadingCreate}
      >
        <Icon
          name="add"
          size={20}
          className="mr-2 mt-[1px]"
        />
        Add Variable
      </Button>

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setDeletingVariable(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Variable"
        message={`Are you sure you want to delete the variable "${deletingVariable?.name}"?`}
      >
        <Text className="text-sm text-gray-600 dark:text-gray-400">This action cannot be undone. The variable will be permanently removed.</Text>
      </ConfirmDialog>
    </View>
  );
};
